import { useEffect, useState, useCallback } from "react";
import { Bell, Check } from "lucide-react";
import api from "@/lib/api";

/**
 * NotificationWatchlist — Settings section for controlling which agents' runs
 * appear in the user's Notification Bell feed. Semantics:
 *   • "All agents" (default) → server-side stored as `null`
 *   • Otherwise → an explicit list of agent ids
 * The "watch none" state is intentionally not exposed in the UI (Phase 0 UX
 * decision — if a user really wants zero notifications, they simply don't
 * click the bell). Deselecting everything falls back to null server-side too.
 */
export default function NotificationWatchlist() {
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState(null); // null = all, or Set of ids
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/watchlist");
      setAvailable(data.available_agents || []);
      setSelected(data.watchlist === null ? null : new Set(data.watchlist));
    } catch {
      setAvailable([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const mode = selected === null ? "all" : "custom";

  const setMode = (m) => {
    if (m === "all") {
      setSelected(null);
    } else {
      // Switching to custom: start with everything selected (matches "all") so
      // deselecting is intuitive.
      setSelected(new Set(available.map((a) => a.id)));
    }
    setDirty(true);
  };

  const toggle = (id) => {
    if (selected === null) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = selected === null ? { watchlist: null } : { watchlist: Array.from(selected) };
      await api.patch("/notifications/watchlist", payload);
      setDirty(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch {
      window.alert("Could not save watchlist. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const allCount = available.length;
  const selCount = selected === null ? allCount : selected.size;

  return (
    <section
      data-testid="notification-watchlist"
      className="bg-white border border-[#e7e9ee] rounded-xl p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="font-display font-semibold text-[15px] text-[#0e1420] inline-flex items-center gap-2">
            <Bell size={15} strokeWidth={1.8} className="text-[#0072bc]" />
            Notification watchlist
          </h3>
          <p className="text-[12.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Which agents&apos; runs light up your Notifications bell. Everyone else&apos;s runs still show up on the Agent Roster page.
          </p>
        </div>
        <div className="text-[11.5px] font-mono-data text-[color:var(--cc-ink-muted)]">
          {selCount}/{allCount} watched
        </div>
      </div>

      <div className="inline-flex bg-[#f5f6f8] rounded-lg p-1 border border-[#e7e9ee] mb-4" data-testid="watchlist-mode">
        {[
          { id: "all", label: "All agents" },
          { id: "custom", label: "Choose specific" },
        ].map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => setMode(o.id)}
            data-testid={`watchlist-mode-${o.id}`}
            className={`px-3 py-1.5 text-[12px] rounded-md font-semibold transition-colors ${
              mode === o.id ? "bg-white text-[#0072bc] shadow-sm" : "text-[color:var(--cc-ink-muted)]"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      {mode === "custom" && (
        <div className="grid sm:grid-cols-2 gap-2" data-testid="watchlist-agent-grid">
          {available.map((a) => {
            const checked = selected?.has(a.id);
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => toggle(a.id)}
                data-testid={`watchlist-agent-${a.id}`}
                aria-pressed={checked}
                className={`text-left flex items-start gap-2.5 p-3 rounded-lg border transition-colors ${
                  checked
                    ? "border-[#0072bc] bg-[#e7f4fb]"
                    : "border-[#e7e9ee] bg-white hover:bg-[#fafbfc]"
                }`}
              >
                <div
                  className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ background: a.avatar_color || "#4c8dff" }}
                  aria-hidden
                >
                  {(a.initial || a.name.slice(0, 1)).slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-semibold text-[#0e1420] truncate">
                    {a.name} <span className="font-mono-data text-[10px] text-[color:var(--cc-ink-muted)]">· {a.agent_code}</span>
                  </div>
                  <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] line-clamp-1">{a.function}</div>
                </div>
                <div
                  className={`shrink-0 w-4 h-4 rounded border-[1.5px] flex items-center justify-center mt-0.5 ${
                    checked ? "border-[#0072bc] bg-[#0072bc]" : "border-[#c9ccd2] bg-white"
                  }`}
                  aria-hidden
                >
                  {checked && <Check size={11} strokeWidth={3} className="text-white" />}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          data-testid="watchlist-save"
          className="cc-btn-primary text-[13px] py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Save watchlist"}
        </button>
        {savedFlash && (
          <span data-testid="watchlist-saved" className="text-[12px] text-[#0e8a68] font-semibold inline-flex items-center gap-1">
            <Check size={13} strokeWidth={2.2} /> Saved
          </span>
        )}
        {!dirty && !savedFlash && (
          <span className="text-[11.5px] text-[color:var(--cc-ink-muted)]">Changes save on Save watchlist</span>
        )}
      </div>
    </section>
  );
}
